import type { NavigateEvent, NavigationTarget } from './api/science/openapi/api';
import {
  Action,
  Calendar,
  CalendarAutoScheduleFrom,
  CalendarEvent,
  CalendarEventShowAs,
  CalendarTaskFrom,
  CalendlyIntegration,
  ChatAdjustmentMealContent,
  ChatSessionType,
  Content,
  ContentPayload,
  Device,
  EMADateSet,
  EnergyFluctuationItem,
  FluctuateReasonType,
  HeatmapDataType,
  Location,
  MealPreference,
  PhaseStartEndSet,
  RichContext,
  Routine,
  RoutineActivity,
  ScheduleEfficiencyScore,
  SchedulerPreference,
  SessionMessage,
  Signal,
  TaskItem,
  TodoIntegrationType,
  TodoTasksBoard,
  Token,
  User,
} from '@demind-inc/core';

export type { Action, Content, ContentPayload, Location, RichContext, Signal };

export enum Status {
  LOADING = 'loading',
  UN_AUTHORIZED = 'unAuthorized',
  AUTHORIZED = 'authorized',
  SIGNING_IN = 'signingIn',
}

export type FirestoreUser = Omit<User, 'userId'>;
export type FirestoreTaskItem = Omit<TaskItem, 'taskId'>;
export type FirestoreCalendar = Omit<Calendar, 'calendarId'>;
export type FirestoreToken = Omit<Token, 'tokenId'>;
export type FirestoreCalendlyInteg = Omit<CalendlyIntegration, 'calendlyIntegrationId'>;
export type FirestoreEMADateSet = Omit<EMADateSet, 'emaDateId'>;
export type FirestoreDevice = Omit<Device, 'deviceId'>;
export type FirestoreRoutine = Omit<Routine, 'routineId'>;
export type FirestoreRoutineActivity = Omit<RoutineActivity, 'routineActivityId'>;

export type PeakDipPhase =
  | 'wakeup_low'
  | 'morning_high'
  | 'morning_low'
  | 'evening_high'
  | 'evening_low';

export type PeakDipStartEndSet = Pick<PhaseStartEndSet, PeakDipPhase>;

export interface CircadianPhaseBoundariesByDate {
  date: string;
  details: PhaseStartEndSet;
}

export interface PeaksDipsBoundariesByDate {
  date: string;
  details: PeakDipStartEndSet;
}

export type CalendarEventCreateOption = Partial<Omit<CalendarEvent, 'updatedDate' | 'createdDate'>>;
export type CalendarEventModifyOption = Partial<
  Omit<CalendarEvent, 'eventId' | 'updatedDate' | 'createdDate' | 'calendarId'>
>;

export type FluctuationSetByReasons = Partial<Record<FluctuateReasonType, EnergyFluctuationItem[]>>;

export interface TasksByDate {
  date: string;
  tasks: TaskItem[];
  updatedAt?: string;
}

export interface CircadianRhythmByDate {
  date: string;
  values: HeatmapDataType[];
  fluctuations?: FluctuationSetByReasons;
}

export type EventDetailsMenuMode = 'edit' | 'energy_boost' | 'activity' | 'temporary';

export type PaymentProvider = 'stripe' | 'revenuecat';

export type AddTodoTaskItemProps = Omit<TaskItem, 'taskId' | 'originalTaskId'>;
export type AddTodoTaskBoardProps = Omit<
  TodoTasksBoard,
  'todoTaskId' | 'originalBoardId' | 'createdAt'
>;

export interface TaskErrorInfo {
  message: string;
  status: number;
  provider?: TodoIntegrationType;
  action?: 'sync' | 'add' | 'delete' | 'update';
}

export type CalendarSnackType = 'success' | 'loading' | 'warning' | 'error';

export type CalendarSnackIcon = 'calendar' | 'task';

export interface CalendarSnackInfo {
  message: string;
  actionLabel?: string;
  autoHideDuration?: number | 'disable';
  onPress?: () => void;
  color?: string;
  type?: CalendarSnackType;
  icon?: CalendarSnackIcon;
}

export interface WearableErrorInfo {
  message: string;
  status: number;
}

export interface PeakDipStartEndSetByDate {
  date: string;
  details: PeakDipStartEndSet;
}
export interface PeakEnergyByDate {
  date: string;
  peakEnergy?: number;
}

export type DataTrendPeriod = 'weekly' | 'daily';

export type RecommendationType = 'default' | 'chronotype';

export type CoreActionOption =
  | 'ai_scheduler'
  | 'energy_boost'
  | 'daily_analysis'
  | 'edit_sleep'
  | 'add_sleep';

export interface CoreActonModalItem {
  id: CoreActionOption;
  title: string;
  description: string;
  component?: React.ComponentType;
  header: string;
  tooltip?: JSX.Element;
}

export interface SleepWakeTimeISOStringSet {
  sleepTime: string; // YYYY-MM-DDTHH:mm:ss
  wakeTime: string; // YYYY-MM-DDTHH:mm:ss
}
export type ViewSwitcherStateType = 'Home' | 'Data';
export type HomeViewSwitcherType = 'Todo' | 'Calendar';
export type CalendarViewType = 'day' | 'week' | 'month';
export type DataMenuItem = 'dataCircadian' | 'dataTask' | 'dataSleep' | 'dataTrend';

export type EnergyLevelType = 'low' | 'normal' | 'high' | 'sleep' | 'wind_down';

export type AISchedulerVersion = 'timebox' | 'todobox';

export type TaskFilterLabelNames = string[] | 'lifestack_all';

export interface TaskFilterOptions {
  labelNames?: TaskFilterLabelNames;
  hideNoDueDate?: boolean;
  onlyMyTask?: boolean;
}

export type MetricActivityType = 'exercise' | 'meal' | 'nap';

export interface ActivityCalendarEvent extends CalendarEvent {
  source?: string;
  activityType?: MetricActivityType;
  steps?: number;
  calories?: number;
  distanceMeters?: number;
  napEfficiency?: number;
  napStages?: Record<string, number>;
  napTemperatureDelta?: number;
}

export type AISchedulerLogicElement = 'timebox' | 'routine' | 'task';

export interface SessionResponse {
  currentSessionId?: string;
  messages?: SessionMessage[];
}

export interface MovedTaskParam {
  prevDate?: string; //YYYY-MM-DD
  newDate?: string; //YYYY-MM-DD
  wasOverdue?: boolean;
}

export type TodoTaskUpdate = Partial<FirestoreTaskItem> & Required<Pick<TaskItem, 'appFrom'>>;

export interface UseUpdateTodoTaskParams {
  boardId: string;
  taskId: string;
  userId: string;
  newTaskInfo: TodoTaskUpdate;
  movedTaskParam?: MovedTaskParam; // Used for updating the state while mutating between dates
}

export interface InsightSuggestionCTA {
  action_type: string;
  payload?: Record<string, unknown>;
}

export interface InsightSuggestion {
  id: string;
  title: string;
  description: string;
  signal_source:
    | 'meal'
    | 'energy'
    | 'sleep'
    | 'weather'
    | 'screen_time'
    | 'commute'
    | 'steps'
    | 'menstrual_cycle'
    | 'medication'
    | 'combined'
    | string;
  urgency: 'now' | 'soon' | 'later' | string;
  expires_at?: string;
  cta_action?: InsightSuggestionCTA;
}

export interface AssistantCommitAction {
  action_id: string;
  status: string;
  tool: string;
  intent_type?: string;
  summary: string;
  payload?: Record<string, unknown>;
  error?: string;
}

// v4 `navigate` SSE event: the assistant asks the app to open a screen or modal.
// The payload and target enum are generated from the science OpenAPI spec
// (NavigateEvent / NavigationTarget). The app owns the target -> screen mapping,
// so unknown (newer) targets must be ignored.
export type AssistantNavigationTarget = NavigationTarget;
export type AssistantNavigation = NavigateEvent;

// RichContext from core + enrichment_pending flag + nearby_places patch
export interface UnifiedChatRichContext extends Omit<RichContext, 'content'> {
  content:
    | (Content & { payload: ContentPayload & { nearby_places?: Record<string, unknown>[] | null } })
    | null;
  reminder_min?: number | null;
  enrichment_pending?: boolean;
  status_text?: string | null;
}

export interface UnifiedChatRichContextEntry {
  date?: string;
  suggestionId: string;
  richContext: UnifiedChatRichContext;
  /** 'adjustment' = came from an adjustments item; 'query' = came from a query field */
  source: 'adjustment' | 'query';
}

// v3 result_json shapes
export interface V3AdjustmentItem {
  rich_context?: UnifiedChatRichContext;
  [key: string]: unknown;
}

export interface V3MealAdjustmentItem extends V3AdjustmentItem {
  name?: string;
  start_time?: string;
  end_time?: string;
  explanation?: string;
}

export interface V3Adjustments {
  schedule?: V3AdjustmentItem[] | null;
  meals?: V3MealAdjustmentItem[] | null;
  sleep?: V3AdjustmentItem | null;
  todo_list?: V3AdjustmentItem[] | null;
  routine_adjustments?: V3AdjustmentItem[] | null;
}

export interface V3UnifiedChatResponseEntry {
  date?: string;
  suggestion_id?: string;
  adjustments?: V3Adjustments;
}

// Shape 1: LLM emitted adjustments — rich_context lives inside each adjustment item
export interface V3ResultJsonAdjustments {
  unified_chat_responses: V3UnifiedChatResponseEntry[];
}

// Shape 2: Pure query (no adjustments) — rich_context lives in query field
export interface V3ResultJsonQuery {
  chat_response?: string;
  adjustments?: V3Adjustments;
  query?: {
    rich_context?: UnifiedChatRichContext;
  };
}

export type V3ResultJson = V3ResultJsonAdjustments | V3ResultJsonQuery;

// SSE payload for enrichment_patch events
export type EnrichmentPatchValue = Record<string, unknown>[] | Record<string, unknown> | boolean;

export interface EnrichmentPatchEvent {
  /** Dotpath to the patched field (e.g. `query.rich_context.content.payload`). */
  field: string;
  /** Human-readable status for progressive UI updates. */
  status_text: string;
  /** Response entry identifier. Usually a date (YYYY-MM-DD) or "flat". */
  suggestion_id: string;
  value: EnrichmentPatchValue;
}

export type ChatSuggestedEventAction =
  | 'checkOnCalendar'
  | 'setEvent'
  | 'setTask'
  | 'setRoutine'
  | 'deleteEvent'
  | 'deleteRoutine'
  | 'deleteTask';

export interface ChatSuggestedEvent {
  eventId: string;
  /** Distinguishes multiple suggestion cards that share an eventId (e.g.
   * same meal type/date suggested more than once). Accept-state keys off
   * this when present; falls back to eventId. Matches lifestack_calendar. */
  blockInstanceId?: string;
  linkedMessageId?: string;
  calendarId?: string;
  name: string;
  date?: string;
  durationMin?: number;
  startTime: string;
  endTime: string;
  description?: string;
  autoScheduleFrom?: CalendarAutoScheduleFrom;
  taskFrom?: CalendarTaskFrom;
  /** Plan-my-week cards keep `autoScheduleFrom.from` as `aiScheduler`. This is
   * the task/routine origin used only to show the title icon. */
  originKind?: 'todo' | 'routine';
  categoryId?: string;
  location?: string;
  before?: {
    /** Present when this adjustment targets an already-committed calendar
     * event (as opposed to a pure pending suggestion) — lets consumers
     * distinguish "update" from "add" even when no other before/after diff
     * is available. */
    id?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    name?: string;
    categoryId?: string;
    mealType?: string;
  };
  recurrence?: {
    string?: string;
  } | null;
  recurring?: RoutineActivity['recurring'];
  isNew?: boolean;
  /** Set on schedule cards pushed into temporalSuggestedEvents from v4
   * result_json — CalendarView uses this + before.id to hide the original
   * committed event while the preview is shown. */
  isTemporalPreview?: boolean;
  action?: ChatSuggestedEventAction;
  mealContent?: ChatAdjustmentMealContent;
  richContext?: UnifiedChatRichContext;
}
export interface AssistantPlanEntryButton {
  id: ChatSessionType | string; // Allow custom IDs for recommendation flow
  text: string;
  action?: string;
}
export interface CTAOption {
  label: string;
  action: string;
  payload: Record<string, unknown>;
  style: 'primary' | 'secondary' | 'destructive';
}

export interface ConversationQuestion {
  context: string;
  question: string;
  options: CTAOption[];
  trigger: string;
  conflicting_event_ids: string[];
  conflicting_event_names: string[];
}

export interface AssistantChatMessage extends SessionMessage {
  ctaButtons?: AssistantPlanEntryButton[];
  suggestedBlocks?: ChatSuggestedEvent[];
  insightSuggestions?: InsightSuggestion[];
  commitActions?: AssistantCommitAction[];
  // v4 only: a navigate event rendered as a tappable "open screen" button.
  navigation?: AssistantNavigation;
  richContexts?: UnifiedChatRichContextEntry[];
  start?: boolean;
  question?: ConversationQuestion;
  isAnswered?: boolean;
  /** Renders as PlanIntroBubble — the "what would you like to plan today?" card. */
  planIntro?: boolean;
  /** Renders as PlanPreviewBubble — the live Plan My Day/replan results card,
   * driven by useAllPlanSuggestedModal() rather than this message's own fields. */
  allPlanPreview?: boolean;
  /** v4 only: in-progress `sequence.status` shown inline before final content. */
  statusText?: string;
  /** v5 stop: bare gray "Cancelled" line on the assistant side. */
  cancelled?: boolean;
}

export interface AssistantChatInputState {
  selectedCTA?: AssistantPlanEntryButton;
  customText: string;
  isEditing: boolean;
}

export interface AssistantConfigSleep {
  defaultCalendarId?: string;
  unSyncToCalendar?: boolean;
}

export interface AssistantConfigMeal extends MealPreference {
  defaultCalendarId?: string;
  unSyncToCalendar?: boolean;
  note?: string;
}

export interface AssistantConfigWorkout {
  defaultCalendarId?: string;
  note?: string;
  disableAutoSchedule?: boolean;
  excludePlanContents?: boolean;
}

export interface AssistantConfigTask {
  defaultCalendarId?: string;
  unSyncToCalendar?: boolean;
}

export interface AssistantConfigEntireDay {
  defaultCalendarId?: string;
  unSyncToCalendar?: boolean;
  transparency?: CalendarEventShowAs;
}

export type SuggestedEventType = ChatSessionType | 'routine' | 'workout';

export type TaskModifyOption = Partial<Omit<TaskItem, 'updatedAt'>>;

export type TaskSidebarViewFilter = 'all' | 'today' | 'this_week' | 'overdue' | 'backlog';
export type TaskSidebarPriorityFilter = 'high' | 'medium' | 'low' | null;

export interface TaskSidebarFilters {
  view: TaskSidebarViewFilter;
  priority: TaskSidebarPriorityFilter;
}
